import React, { SetStateAction, useEffect, useState } from "react";
import {
  Box,
  Checkbox,
  Typography,
  Button,
  Paper,
  Stack,
  useTheme,
  Tooltip,
} from "@mui/material";
import { useDispatch , useSelector } from "react-redux";
import { KNOWLEDGE_BASE } from "../../../Services/Endpoint";
import BaseInstance from "../../../Services/AxiosInstance/BaseInstance";
import {
  StartLoading,
  StopLoading,
} from "../../../Shared-ui/Store/LoadingReducer/LoaderActions";
import { ShowError } from "../../../Shared-ui/Store/MessageReducer/MsgActions";
import { KnowledgeBase } from "../../../Shared-ui/Types/KnowledgeBase";
import { Agent } from "../../../Shared-ui/Types/Agent";
import EmptyStateImage from "../../../assets/images/Agents_emptystateimage.svg";
import SearchIcon from "@mui/icons-material/Search";
import TextField from "@mui/material/TextField";
import { useNavigate } from "react-router-dom";
import variables from "../../../assets/styles/variables.module.scss";

interface MyComponentProps {
  Agent: Agent;
  setAgent: React.Dispatch<SetStateAction<Agent>>;
  AgentErr: any;
}

const KnowledgeTab: React.FC<MyComponentProps> = (props) => {
  const [checked, setChecked] = React.useState<number[]>([]);
  const theme = useTheme();
  const dispatch = useDispatch();
  const [knowledgeBases, setKnowledgeBases] = useState<KnowledgeBase[]>([]);
  const loaderStatus = useSelector((state: any) => state.LoaderReducer);


  useEffect(() => {
    getKnowledgeBases();
  }, []);

  const navigate = useNavigate();

  const getKnowledgeBases = async () => {
    try {
      dispatch(StartLoading("KnowledgeBase"));
      const data = await BaseInstance.get(
        `${KNOWLEDGE_BASE}?skip=${0}&limit=${9999}`
      ).then((res) => res?.data);
      data.sort((a: { name: string }, b: { name: string }) =>
        a.name.localeCompare(b.name)
      );
      setKnowledgeBases(data);
    } catch (err: any) {
      dispatch(ShowError(err?.response?.data?.statusText));
    } finally {
      dispatch(StopLoading("KnowledgeBase"));
    }
  };

  const handleSelectKW = (knowledge: KnowledgeBase) => {
    props.setAgent((preVal) => {
      const knowledgeBaseIds = preVal.knowledge_base_ids;
      if (knowledge.id) {
        const newKwIds = knowledgeBaseIds.includes(knowledge.id)
          ? knowledgeBaseIds.filter((id) => id !== knowledge.id)
          : [...knowledgeBaseIds, knowledge.id];
        return {
          ...preVal,
          knowledge_base_ids: newKwIds,
        };
      } else {
        return {
          ...preVal,
          knowledge_base_ids: knowledgeBaseIds,
        };
      }
    });
  };

  const handleClearAll = () => {
    props.setAgent((preVal) => {
      return {
        ...preVal,
        knowledge_base_ids: [],
      };
    });
  };

  const [searchKnowledge, setSearchKnowledge] = useState<string>("");
  const isKnowledgeLoading = loaderStatus.isLoading && loaderStatus.pageName === "KnowledgeBase";
  const noKnowledgeFound = !isKnowledgeLoading && knowledgeBases.length === 0;


  return (
    <Box sx={{}}>
      <Typography
        variant="subtitle2"
        sx={{ display: { xs: "block", md: "none" }, mb: 2, mt: 1 }}
      >
        Knowledge
      </Typography>
      <Box p={theme.spacing(0)} sx={{ maxWidth: "100%" }}>
        {isKnowledgeLoading || noKnowledgeFound ? (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              mt: 6,
              cursor: isKnowledgeLoading ? "default" : "pointer",
            }}
          >
            <Tooltip title={isKnowledgeLoading ? "Knowledge base is loading..." : "No knowledge base found"} arrow>
              <img
                src={EmptyStateImage}
                alt={isKnowledgeLoading ? "Knowledge loading" : "No Knowledge Found"}
                style={{ width: 120, height: "auto" }}
              />
            </Tooltip>
            <Typography
              variant="h6"
              sx={{
                mt: 2,
                textDecoration: "underline",
                cursor: "pointer",
                color: variables.basicColor,
              }}
              {...(noKnowledgeFound && { onClick: () => navigate("/knowledge-bases") })}
            >
              {isKnowledgeLoading
              ? "Knowledge loading..."
              : "Click here to Create Knowledge"}
            </Typography>
          </Box>
        ) : (
          <>

            {/* Styled Paper Wrapper */}
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Typography variant="subtitle2" mb={1}>
              Available Knowledge
            </Typography>
              <TextField
                placeholder="Search Knowledge"
                value={searchKnowledge}
                onChange={(e) => setSearchKnowledge(e.target.value)}
                fullWidth
                sx={{ mb: 1 }}
                InputProps={{
                  startAdornment: (
                    <SearchIcon sx={{ color: "text.secondary", mr: 1 }} />
                  ),
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  mb: 1,
                }}
              >
                <Typography variant="subtitle1" fontWeight="500">
                  Selected Knowledge ({props.Agent.knowledge_base_ids.length})
                </Typography>
                {/* Clear All Button */}
                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    variant="text"
                    onClick={handleClearAll}
                    disabled={props.Agent.knowledge_base_ids.length === 0}
                    sx={{
                      textTransform: "capitalize",
                    }}
                  >
                    Clear All
                  </Button>
                </Stack>
              </Box>
              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns: {
                    xs: "1fr",
                    sm: "1fr 1fr",
                    md: "1fr 1fr 1fr",
                  },
                  gap: 2,
                }}
              >
                {knowledgeBases
                  .filter((knowledgeBase) =>
                    knowledgeBase.name
                      .toLowerCase()
                      .includes(searchKnowledge.toLowerCase())
                  )
                  .map((knowledgeBase) => (
                    <React.Fragment key={knowledgeBase.id}>
                      {knowledgeBase.id && (
                        <Stack
                          direction="row"
                          alignItems="center"
                          spacing={1}
                          sx={{
                            cursor: "pointer",
                            border: "1px solid #ccc",
                            p: 1,
                            borderRadius: 1,
                            transition: "border-color 0.2s, background 0.2s",
                            "&:hover": {
                              borderColor: "primary.main",
                              background: "#f5faff",
                            },
                          }}
                          onClick={() => handleSelectKW(knowledgeBase)}
                        >
                          <Checkbox
                            checked={props.Agent.knowledge_base_ids.includes(
                              knowledgeBase.id
                            )}
                            onClick={(e) => e.stopPropagation()} // Prevent Stack's onClick when clicking the checkbox
                            onChange={() => handleSelectKW(knowledgeBase)}
                            disableRipple
                          />
                          <Typography
                            variant="body1"
                            sx={{
                              textTransform: "capitalize",
                            }}
                          >
                            {knowledgeBase.name}
                          </Typography>
                        </Stack>
                      )}
                    </React.Fragment>
                  ))}
              </Box>
            </Paper>
          </>
        )}

        {/* Selected Knowledge Bases */}
        {/* {checked.length > 0 ? (
        checked.map((id) => (
          <Typography key={id} variant="body2">
            • {knowledgeBases.find((kb) => kb.id === id)?.label}
          </Typography>
        ))
      ) : (
        <Typography variant="body2" color="text.secondary">
          No knowledge bases selected
        </Typography>
      )} */}
      </Box>
    </Box>
  );
};

export default KnowledgeTab;
function dispatch(arg0: any) {
  throw new Error("Function not implemented.");
}
